import { BadgeCheck, Bot, UserRound, Workflow } from 'lucide-react';

import { cn } from '@/lib/utils';

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

import { SOURCE_HINTS } from './ClaudeDraftBanner';

const ICONS = { Person: UserRound, Claude: Bot, Pipeline: Workflow };

type Origin = keyof typeof SOURCE_HINTS;

/** The tooltip sentence for a row's origin, naming the person when we know who edited it. */
function sourceHint(origin: Origin, editedBy: string | null): string {
    if (origin === 'Person') {
        return editedBy
            ? `Made by ${editedBy}. They set these pages and the type by hand.`
            : SOURCE_HINTS.Person;
    }
    return editedBy
        ? `${SOURCE_HINTS[origin]} Edited by ${editedBy}.`
        : SOURCE_HINTS[origin];
}

/** The one-line state of a row: who verified it, who edited it, or that it is not verified yet. */
function stateLine(
    origin: Origin,
    checked: boolean,
    editedBy: string | null,
    checkedBy: string | null,
): string {
    if (checked) return `Verified by ${checkedBy ?? 'a person'}`;
    if (origin === 'Person')
        return `Edited by ${editedBy ?? 'a person'} · not verified`;
    return 'Not verified yet';
}

/** Who made a row, as a round icon with the full explanation in a tooltip and the aria-label. */
export function SourceMark({
    origin,
    checked,
    editedBy = null,
    checkedBy = null,
    testId,
}: {
    origin: Origin;
    checked: boolean;
    editedBy?: string | null;
    checkedBy?: string | null;
    testId: string;
}) {
    const Icon = checked ? BadgeCheck : ICONS[origin];
    const hint = sourceHint(origin, editedBy);
    const state = stateLine(origin, checked, editedBy, checkedBy);
    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span
                        role="img"
                        tabIndex={0}
                        aria-label={`${hint} ${state}`}
                        className={cn(
                            'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                            checked
                                ? 'bg-green-100 text-green-700'
                                : origin === 'Claude'
                                  ? 'bg-slate-50 text-slate-600'
                                  : 'bg-[#EFF9F9] text-[#476969]',
                        )}
                        data-testid={testId}
                    >
                        <Icon aria-hidden="true" className="h-3.5 w-3.5" />
                    </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px] space-y-1 text-xs">
                    <p>{hint}</p>
                    <p className="font-semibold">{state}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
